<template>
  <w-modal
    v-model="isOpen"
    size="large"
    class="iframe_modal"
    title="Transcrição"
    :close-on-click-outside="false"
    style="z-index: 999999999"
    @close="onModalClose"
  >
    <template #body>
      <div class="iframe-container">
        <!-- Skeleton loader overlay -->
        <div
          v-if="props.showSkeleton && props.iframeUrl && !props.iframeError"
          class="skeleton-overlay"
          :class="{ 'fade-out': props.iframeLoaded }"
        >
          <div class="skeleton-loader">
            <!-- Header skeleton -->
            <div class="skeleton-header">
              <w-skeleton :height="24" shape="default" :width="120" />
              <w-skeleton :height="20" shape="circle" :width="20" />
            </div>

            <!-- Main content area skeleton -->
            <div class="skeleton-content">
              <w-skeleton :height="16" shape="default" :width="280" />
              <w-skeleton :height="16" shape="default" :width="240"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="200"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="320"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="180"
class="skeleton-item" />
            </div>

            <!-- Control buttons skeleton -->
            <div class="skeleton-controls">
              <w-skeleton :height="40" shape="default" :width="120" />
              <w-skeleton :height="40" shape="default" :width="100" />
            </div>

            <!-- Status indicator skeleton -->
            <div class="skeleton-status">
              <w-skeleton :height="12" shape="circle" :width="12" />
              <w-skeleton :height="14" shape="default" :width="80" />
            </div>
          </div>

          <div class="loading-text">
            <w-spinner size="large" />
            <w-text type="bodyEmphasis">Carregando o gravador...</w-text>
          </div>
        </div>

        <!-- Iframe content -->
        <iframe
          v-if="props.iframeUrl"
          :key="props.iframeKey"
          :src="props.iframeUrl"
          class="transcription-iframe"
          :class="{ 'fade-in': props.iframeLoaded }"
          frameborder="0"
          allowfullscreen
          allow="microphone; camera; clipboard-write; autoplay; encrypted-media; display-capture; screen-wake-lock; fullscreen"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-top-navigation allow-top-navigation-by-user-activation"
          @load="onIframeLoad"
          @error="onIframeError"
          @loadstart="onIframeLoadStart"
        ></iframe>

        <!-- Error state -->
        <div v-else-if="props.iframeError" class="error-iframe">
          <w-icon name="alert-circle" color="error" size="large" />
          <p>{{ props.iframeError }}</p>
          <w-button
            @click="retryIframe"
            variant="outline"
            size="small"
            data-dd-action-name="noa_recorder_retry_iframe"
          >
            Tentar novamente
          </w-button>
        </div>

        <!-- Initial loading state (when no iframeUrl yet) -->
        <div v-else class="loading-iframe">
          <div class="skeleton-loader">
            <!-- Header skeleton -->
            <div class="skeleton-header">
              <w-skeleton :height="24" shape="default" :width="120" />
              <w-skeleton :height="20" shape="circle" :width="20" />
            </div>

            <!-- Main content area skeleton -->
            <div class="skeleton-content">
              <w-skeleton :height="16" shape="default" :width="280" />
              <w-skeleton :height="16" shape="default" :width="240"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="200"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="320"
class="skeleton-item" />
              <w-skeleton :height="16" shape="default" :width="180"
class="skeleton-item" />
            </div>

            <!-- Control buttons skeleton -->
            <div class="skeleton-controls">
              <w-skeleton :height="40" shape="default" :width="120" />
              <w-skeleton :height="40" shape="default" :width="100" />
            </div>

            <!-- Status indicator skeleton -->
            <div class="skeleton-status">
              <w-skeleton :height="12" shape="circle" :width="12" />
              <w-skeleton :height="14" shape="default" :width="80" />
            </div>
          </div>

          <div class="loading-text">
            <w-spinner size="small" />
            <p>Carregando transcrição...</p>
          </div>
        </div>
      </div>
    </template>
  </w-modal>
</template>

<style scoped lang="scss">
@import "style-variables.scss";

.iframe-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
}

.transcription-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.transcription-iframe.fade-in {
  opacity: 1;
}

.skeleton-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.skeleton-overlay.fade-out {
  opacity: 0;
  pointer-events: none;
}

.loading-iframe {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;
  gap: 24px;
}

.skeleton-loader {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px;
  background-color: var(--w-background-color);
  border-radius: 8px;
  border: 1px solid var(--w-border-color);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

.skeleton-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--w-border-color);
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-item {
  margin-top: 8px;
}

.skeleton-controls {
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 16px 0;
  border-top: 1px solid var(--w-border-color);
}

.skeleton-status {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  padding: 12px;
  background-color: var(--w-background-color-secondary);
  border-radius: 6px;
}

.loading-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background-color: var(--w-background-color-secondary);
  border-radius: 8px;
  border: 1px solid var(--w-border-color);
}

.loading-text p {
  margin: 0;
  font-size: 14px;
  color: var(--w-text-color-secondary);
  text-align: center;
}

.spinner-placeholder {
  width: 20px;
  height: 20px;
  border: 2px solid var(--w-border-color);
  border-top-color: var(--w-text-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-iframe {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 16px;
  color: var(--w-text-color-error);
  text-align: center;
  padding: 24px;
}

.error-iframe p {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
}

/* Garantir que o modal fique acima de todos os elementos */
::deep(.w-modal) {
  z-index: 999999999 !important;
}

::deep(.w-modal-backdrop) {
  z-index: 999999998 !important;
}
</style>

<style>
.w-modal.iframe_modal {
  position: fixed;
  right: 0px;
  bottom: 0;
  height: min-content;
  width: fit-content;
  left: auto;
  top: auto;
  z-index: 999999999 !important;
}

.iframe_modal > div {
  width: 480px !important;
  height: 590px !important;
  max-width: none !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.w-modal.iframe_modal:after {
  display: none !important;
}
.iframe_modal .w-modal-body {
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Ensure the original modal header is visible so we can swap its icon */
/* Removed rules that hide .w-modal-header and .w-modal-title */

.w-modal-open {
  overflow: auto !important;
}

/* Hide scrollbars for the transcription modal */
.iframe_modal,
.iframe_modal * {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.iframe_modal::-webkit-scrollbar,
.iframe_modal *::-webkit-scrollbar {
  display: none; /* WebKit browsers (Chrome, Safari, Edge) */
}
</style>

<script setup lang="ts">
/**
 * AIAssistantTranscriptionModal.vue - Recorder Modal
 *
 * Modal containing the iframe for recording transcriptions.
 * Handles microphone permissions and communication with the recorder iframe.
 */
import { identifyEnvironment } from "@/utils/IdentifyGlobalEnv";
import { UserTracking } from "@/utils/UserTracking";
import { trackNoaRumAction, NOA_RUM_EVENT } from "@/utils/noaObservability";
import { ref, watch, onMounted, onUnmounted, nextTick, inject } from "vue";

const safeIframeHost = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return "unknown";
  }
};

interface ApiClient {
  get: (url: string, params?: any) => Promise<any>;
  post: (url: string, data?: any) => Promise<any>;
  put: (url: string, data?: any) => Promise<any>;
}

interface Props {
  isOpen: boolean;
  iframeUrl: string;
  iframeLoaded: boolean;
  showSkeleton: boolean;
  iframeError: string | null;
  iframeKey: number;
  sessionId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits([
  "close",
  "modal-opened",
  "modal-closed",
  "recorder-ready",
  "recording-started",
  "recording-finished",
  "iframe-load",
  "iframe-load-start",
  "iframe-error",
  "start-new-session",
]);

const apiClient = inject<ApiClient | null>("apiClient", null);
const isAbandoning = ref(false);

const isOpen = ref(props.isOpen);
const microphoneStream = ref<MediaStream | null>(null);

// Replace the default close icon with a minus icon inside the modal header
const replaceModalCloseIcon = () => {
  try {
    const modalRoot = document.querySelector(".w-modal.iframe_modal") as HTMLElement | null;
    if (!modalRoot) return;

    const closeBtn = modalRoot.querySelector(".w-icon-close") as HTMLElement | null;
    if (!closeBtn) return;

    // Try to find an existing svg and replace its content
    const svgEl = closeBtn.querySelector("svg") as SVGElement | null;
    const minusPath = '<path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>';

    if (svgEl) {
      svgEl.setAttribute("viewBox", "0 0 24 24");
      svgEl.innerHTML = minusPath;
      return;
    }

    // If no svg found, inject one
    const newSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    newSvg.setAttribute("width", "24");
    newSvg.setAttribute("height", "24");
    newSvg.setAttribute("viewBox", "0 0 24 24");
    newSvg.setAttribute("fill", "none");
    newSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    newSvg.innerHTML = minusPath;

    closeBtn.innerHTML = "";
    closeBtn.appendChild(newSvg);
  } catch (_) {
    // no-op
  }
};

// Request microphone permission in parent page to establish permission for iframe
const requestMicrophonePermission = async () => {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn("getUserMedia is not supported in this browser");
      return;
    }

    // Check if we already have permission
    try {
      const permissionStatus = await navigator.permissions.query({ name: "microphone" as PermissionName });

      if (permissionStatus?.state === "granted" && microphoneStream.value) {
        console.log("✅ Microphone permission already granted and stream active");
        return;
      }
    } catch (e) {
      // Permissions API might not be supported, continue with request
      console.log("Permissions API not available, proceeding with getUserMedia");
    }

    // Stop any existing stream first
    if (microphoneStream.value) {
      microphoneStream.value.getTracks().forEach((track) => track.stop());
      microphoneStream.value = null;
    }

    // Request microphone access - this will prompt the user and establish permission
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Keep the stream alive - don't stop it immediately
    // Stopping the stream can cause the browser to revoke permission
    microphoneStream.value = stream;
    console.log("✅ Microphone permission established in parent page, keeping stream alive");

    // Notify iframe that permission is granted
    const iframe = document.querySelector(".transcription-iframe") as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage(
          {
            type: "PARENT_MICROPHONE_PERMISSION_GRANTED",
            hasStream: true,
          },
          "*",
        );
      } catch (e) {
        console.warn("Could not notify iframe:", e);
      }
    }
  } catch (error: any) {
    console.error("Failed to request microphone permission:", error);
  }
};

// Watch for prop changes
watch(
  () => props.isOpen,
  async (newValue) => {
    isOpen.value = newValue;
    if (newValue) {
      emit("modal-opened");
      await nextTick();
      replaceModalCloseIcon();
      injectDropdownMenu();

      // Note: We don't request permission here because iframes don't inherit parent permissions
      // The iframe will request permission itself when it loads
      // The Permissions Policy meta tag and allow attribute enable the iframe to request permission
    }
  },
);

// Watch for internal state changes
watch(isOpen, async (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    emit("modal-closed");
    // Stop microphone stream when modal closes
    if (microphoneStream.value) {
      microphoneStream.value.getTracks().forEach((track) => track.stop());
      microphoneStream.value = null;
      console.log("🎤 Microphone stream stopped on modal close");
    }
  }
  if (newValue === true) {
    await nextTick();
    replaceModalCloseIcon();
    injectDropdownMenu();

    // Note: We don't request permission here because iframes don't inherit parent permissions
    // The iframe will request permission itself when it loads
  }
});

const onModalClose = () => {
  UserTracking.trackEvent("noa_recorder_modal_closed", {
    session_id: props.sessionId ?? "unknown",
    iframe_loaded: String(props.iframeLoaded),
    iframe_error: props.iframeError ?? "none",
    page_url: window.location.href,
  });
  trackNoaRumAction(NOA_RUM_EVENT.MODAL_CLOSED, {
    session_id: props.sessionId ?? "unknown",
    iframe_loaded: props.iframeLoaded,
    iframe_error: props.iframeError ?? null,
  });

  // Don't reset iframeLoaded state when closing - keep it for when modal reopens
  // Force close the iframe before closing modal to prevent dialogs
  forceCloseIframe();

  // Stop microphone stream when closing modal
  if (microphoneStream.value) {
    microphoneStream.value.getTracks().forEach((track) => track.stop());
    microphoneStream.value = null;
    console.log("🎤 Microphone stream stopped on modal close");
  }

  isOpen.value = false;
};

// Force close iframe without any dialogs
const forceCloseIframe = () => {
  UserTracking.trackEvent("noa_recorder_iframe_destroyed", {
    session_id: props.sessionId ?? "unknown",
    reason: "force_close",
    page_url: window.location.href,
  });
  trackNoaRumAction(NOA_RUM_EVENT.IFRAME_DESTROYED, {
    session_id: props.sessionId ?? "unknown",
    reason: "force_close",
  });

  // This is now handled by the parent component
  // Just emit the close event
  emit("close");
};

// Gracefully finish and close the iframe recorder
const finishAndClose = () => {
  try {
    const iframe = document.querySelector(".transcription-iframe") as HTMLIFrameElement | null;
    if (iframe && iframe.contentWindow) {
      // Ask the iframe app to gracefully stop any ongoing recording and release devices
      try {
        iframe.contentWindow.postMessage({ type: "FINISH_AND_CLOSE" }, "*");
      } catch (_) {
        /* ignore */
      }

      // Prevent iframe from showing beforeunload dialogs
      try {
        iframe.contentWindow.postMessage({ type: "PREVENT_UNLOAD_DIALOG" }, "*");
      } catch (_) {
        /* ignore */
      }
    }

    // Force close by clearing the src which releases media tracks and prevents dialogs
    if (iframe) {
      // Remove the iframe from DOM to prevent any further interactions
      iframe.remove();
    }
  } catch (_) {
    /* ignore */
  }

  // Close modal
  isOpen.value = false;
};

const onIframeLoad = async () => {
  console.log("onIframeLoad called - iframe loaded successfully");

  UserTracking.trackEvent("noa_recorder_iframe_loaded", {
    session_id: props.sessionId ?? "unknown",
    iframe_url_host: safeIframeHost(props.iframeUrl),
    page_url: window.location.href,
  });
  trackNoaRumAction(NOA_RUM_EVENT.IFRAME_LOADED, {
    session_id: props.sessionId ?? "unknown",
    iframe_url_host: safeIframeHost(props.iframeUrl),
  });

  emit("iframe-load");

  // Note: Iframes don't inherit microphone permissions from parent pages
  // The iframe will request permission itself when it needs it
  // The Permissions Policy meta tag and allow attribute enable the iframe to request permission
  // We don't need to request permission here - let the iframe handle it
};

const onIframeLoadStart = () => {
  console.log("onIframeLoadStart called - iframe started loading");
  emit("iframe-load-start");
};

const onIframeError = (event: Event) => {
  console.error("Transcription iframe failed to load:", event);

  UserTracking.trackEvent("noa_recorder_iframe_error", {
    session_id: props.sessionId ?? "unknown",
    iframe_url_host: safeIframeHost(props.iframeUrl),
    event_type: event.type ?? "unknown",
    page_url: window.location.href,
  });
  trackNoaRumAction(NOA_RUM_EVENT.IFRAME_ERROR, {
    session_id: props.sessionId ?? "unknown",
    iframe_url_host: safeIframeHost(props.iframeUrl),
    event_type: event.type ?? "unknown",
  });

  emit("iframe-error", event);
};

const retryIframe = () => {
  // Emit event to parent to handle retry logic
  emit("iframe-error", new Event("retry"));
};

// Handle messages from the iframe
const handleIframeMessage = (event: MessageEvent) => {
  // Only handle messages from the transcription iframe
  const iframe = document.querySelector(".transcription-iframe") as HTMLIFrameElement;
  if (!iframe || event.source !== iframe.contentWindow) {
    return;
  }

  try {
    const data = event.data;

    switch (data.type) {
      case "REQUEST_MICROPHONE_PERMISSION":
        // Iframe is requesting microphone permission - trigger it in parent page
        console.log("Iframe requested microphone permission, requesting in parent page");
        requestMicrophonePermission();
        break;

      case "MICROPHONE_PERMISSION_DENIED":
        console.warn("Microphone permission denied by iframe:", data.reason);
        UserTracking.trackEvent("noa_recorder_mic_permission_denied", {
          session_id: props.sessionId ?? "unknown",
          reason: data.reason ?? "unknown",
          page_url: window.location.href,
        });
        trackNoaRumAction(NOA_RUM_EVENT.MIC_PERMISSION_DENIED, {
          session_id: props.sessionId ?? "unknown",
          reason: data.reason ?? "unknown",
        });
        // Try requesting permission again in parent page
        console.log("Attempting to request microphone permission in parent page");
        requestMicrophonePermission();
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
          console.info("Tip: On localhost, ensure your browser allows microphone access for this site");
        }
        break;

      case "DEVICE_NOT_FOUND":
        console.warn("Audio device not found:", data.deviceType);
        UserTracking.trackEvent("noa_recorder_device_not_found", {
          session_id: props.sessionId ?? "unknown",
          device_type: data.deviceType ?? "unknown",
          page_url: window.location.href,
        });
        trackNoaRumAction(NOA_RUM_EVENT.DEVICE_NOT_FOUND, {
          session_id: props.sessionId ?? "unknown",
          device_type: data.deviceType ?? "unknown",
        });
        // Emit error to parent component
        emit("iframe-error", new Event("device-not-found"));
        if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
          console.info("Tip: On localhost, ensure your system has a working microphone");
        }
        break;

      case "IFRAME_READY":
        trackNoaRumAction(NOA_RUM_EVENT.RECORDER_READY, {
          session_id: props.sessionId ?? "unknown",
        });
        emit("recorder-ready");
        break;

      case "recording-started":
        // Do not forward raw iframe data; parent only needs the lifecycle signal.
        trackNoaRumAction(NOA_RUM_EVENT.RECORDING_STARTED, {
          session_id: props.sessionId ?? "unknown",
          source: "iframe_post_message",
        });
        emit("recording-started");
        break;

      case "IFRAME_REQUEST_CLOSE":
        UserTracking.trackEvent("noa_recorder_close_requested", {
          session_id: props.sessionId ?? "unknown",
          accepted: "false",
          page_url: window.location.href,
        });
        trackNoaRumAction(NOA_RUM_EVENT.CLOSE_REQUESTED, {
          session_id: props.sessionId ?? "unknown",
          accepted: false,
        });
        // Prevent iframe from closing itself - we control the modal closing
        if (identifyEnvironment() === "local" || identifyEnvironment() === "staging") {
          console.log("Iframe requested to close. Bypassing.");
        }
        // Optionally send a message back to acknowledge but don't close
        try {
          iframe.contentWindow?.postMessage({ type: "CLOSE_REQUEST_ACKNOWLEDGED" }, "*");
        } catch (_) {
          /* ignore */
        }
        break;

      case "IFRAME_FINISHED":
        UserTracking.trackEvent("noa_recorder_finished", {
          session_id: props.sessionId ?? "unknown",
          page_url: window.location.href,
        });
        trackNoaRumAction(NOA_RUM_EVENT.FINISHED, {
          session_id: props.sessionId ?? "unknown",
        });
        // Iframe finished its process, we can close the modal
        console.log("Iframe finished its process, closing modal");
        isOpen.value = false;
        break;

      case "recording-finished":
        UserTracking.trackEvent("noa_recorder_recording_finished", {
          session_id: props.sessionId ?? "unknown",
          duration_ms: String(data.data?.duration_ms ?? ""),
          audio_chunks: String(data.data?.chunks_count ?? ""),
          page_url: window.location.href,
        });
        trackNoaRumAction(NOA_RUM_EVENT.RECORDING_FINISHED_IFRAME, {
          session_id: props.sessionId ?? "unknown",
          duration_ms: data.data?.duration_ms,
          audio_chunks: data.data?.chunks_count,
        });
        // Recording finished - emit event to parent to handle the transition
        console.log("Recording finished, notifying parent component");
        emit("recording-finished", data.data);
        break;

      default:
        // Surface unknown iframe-originated message types so a new NOA-side
        // contract (e.g. a fresh `noaStatus` value) is detectable without a
        // code change. Aggregating by `message_type` answers
        // "which messages reach the FE and go uninstrumented".
        trackNoaRumAction(NOA_RUM_EVENT.UNKNOWN_IFRAME_MESSAGE, {
          session_id: props.sessionId ?? "unknown",
          message_type: typeof data?.type === "string" ? data.type : "non_string_type",
        });
        if (identifyEnvironment() === "local" || identifyEnvironment() === "staging") {
          console.log("Received message from iframe:", data);
        }
    }
  } catch (error) {
    console.warn("Error handling iframe message:", error);
  }
};

// Listener para eventos de teclado (ESC) quando o modal estiver aberto
const handleKeyDown = (event: KeyboardEvent) => {
  if (isOpen.value && event.key === "Escape") {
    isOpen.value = false;
  }
};

// Prevent iframe from showing beforeunload dialogs
const handleBeforeUnload = (event: BeforeUnloadEvent) => {
  // Only prevent if the event is coming from our iframe
  const iframe = document.querySelector(".transcription-iframe") as HTMLIFrameElement;
  if (iframe && event.target === iframe.contentWindow) {
    event.preventDefault();
    event.returnValue = "";
    return "";
  }
};

// Adicionar e remover listeners quando o modal abrir/fechar
watch(isOpen, (isModalOpen) => {
  if (isModalOpen) {
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("message", handleIframeMessage);
    window.addEventListener("beforeunload", handleBeforeUnload);
  } else {
    document.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("message", handleIframeMessage);
    window.removeEventListener("beforeunload", handleBeforeUnload);
  }
});

onMounted(async () => {
  // Add message listener on mount
  window.addEventListener("message", handleIframeMessage);
  window.addEventListener("beforeunload", handleBeforeUnload);
  await nextTick();
  replaceModalCloseIcon();
  injectDropdownMenu();
});

onUnmounted(() => {
  // Limpar listeners quando o componente for desmontado
  document.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("message", handleIframeMessage);
  window.removeEventListener("beforeunload", handleBeforeUnload);

  // Stop microphone stream on unmount
  if (microphoneStream.value) {
    microphoneStream.value.getTracks().forEach((track) => track.stop());
    microphoneStream.value = null;
  }
});

/**
 * Handle starting a new session - abandons current session and emits event
 */
const handleStartNewSession = async () => {
  if (!props.sessionId || !apiClient) {
    console.warn("[TranscriptionModal] Cannot abandon session - missing sessionId or apiClient");
    emit("start-new-session");
    return;
  }

  isAbandoning.value = true;

  try {
    // Call API to abandon the current session
    await apiClient.put(`/session/${props.sessionId}/abandon`);
    console.log("[TranscriptionModal] Session abandoned successfully");
  } catch (error) {
    console.error("[TranscriptionModal] Error abandoning session:", error);
    // Continue anyway - we want to allow starting a new session even if abandon fails
  }

  isAbandoning.value = false;

  // Force close the iframe
  forceCloseIframe();

  // Close modal
  isOpen.value = false;

  // Emit event to parent to start new session flow
  emit("start-new-session");
};

/**
 * Inject the dropdown menu into the modal header
 */
const injectDropdownMenu = () => {
  try {
    const modalRoot = document.querySelector(".w-modal.iframe_modal") as HTMLElement | null;
    if (!modalRoot) return;

    const header = modalRoot.querySelector(".w-modal-header") as HTMLElement | null;
    if (!header) return;

    // Check if dropdown already exists
    if (header.querySelector(".recorder-dropdown-container")) return;

    // Create dropdown container
    const dropdownContainer = document.createElement("div");
    dropdownContainer.className = "recorder-dropdown-container";
    dropdownContainer.style.cssText =
      "position: absolute; right: 40px; top: 50%; transform: translateY(-50%); z-index: 10;";

    // Create dropdown button - smaller, with hover effects
    const dropdownBtn = document.createElement("button");
    dropdownBtn.className = "recorder-dropdown-btn";
    dropdownBtn.type = "button";
    dropdownBtn.setAttribute("data-dd-action-name", "noa_recorder_menu_toggle");
    dropdownBtn.style.cssText = `
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: all 0.15s ease;
    `;
    dropdownBtn.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="6" r="1.5" fill="currentColor"/>
        <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
        <circle cx="12" cy="18" r="1.5" fill="currentColor"/>
      </svg>
    `;

    // Add hover effects to button
    dropdownBtn.addEventListener("mouseenter", () => {
      dropdownBtn.style.background = "rgba(0, 0, 0, 0.05)";
      dropdownBtn.style.color = "#374151";
    });
    dropdownBtn.addEventListener("mouseleave", () => {
      dropdownBtn.style.background = "transparent";
      dropdownBtn.style.color = "#6b7280";
    });

    // Create dropdown menu - compact and polished
    const dropdownMenu = document.createElement("div");
    dropdownMenu.className = "recorder-dropdown-menu";
    dropdownMenu.style.cssText = `
      display: none;
      position: absolute;
      right: 0;
      top: calc(100% + 4px);
      background: white;
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
      min-width: 160px;
      z-index: 1000;
      overflow: hidden;
      border: 1px solid rgba(0, 0, 0, 0.08);
    `;

    // Create menu item - compact styling
    const menuItem = document.createElement("button");
    menuItem.className = "recorder-dropdown-item";
    menuItem.type = "button";
    menuItem.setAttribute("data-dd-action-name", "noa_recorder_start_new_session");
    menuItem.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
      font-size: 13px;
      color: #374151;
      transition: all 0.12s ease;
      font-family: inherit;
    `;
    menuItem.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
      </svg>
      <span>Iniciar nova sessão</span>
    `;

    // Add hover effect to menu item
    menuItem.addEventListener("mouseenter", () => {
      menuItem.style.background = "#f3f4f6";
      menuItem.style.color = "#111827";
    });
    menuItem.addEventListener("mouseleave", () => {
      menuItem.style.background = "transparent";
      menuItem.style.color = "#374151";
    });

    // Add click handler
    menuItem.addEventListener("click", () => {
      dropdownMenu.style.display = "none";
      handleStartNewSession();
    });

    dropdownMenu.appendChild(menuItem);
    dropdownContainer.appendChild(dropdownBtn);
    dropdownContainer.appendChild(dropdownMenu);

    // Toggle dropdown on button click
    dropdownBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      const isVisible = dropdownMenu.style.display === "block";
      dropdownMenu.style.display = isVisible ? "none" : "block";
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", () => {
      dropdownMenu.style.display = "none";
    });

    // Make header position relative for absolute positioning
    header.style.position = "relative";

    // Insert dropdown before the close button
    header.appendChild(dropdownContainer);
  } catch (error) {
    console.warn("[TranscriptionModal] Error injecting dropdown menu:", error);
  }
};

// Expose imperative API to parent components
defineExpose({ finishAndClose, requestMicrophonePermission });
</script>
